/**
 * Hierarchical State Machine (HSM) for Entity Locomotion and Stance Management.
 *
 * Enforces validated transitions across:
 * - Environment: ground | water | air
 * - Stance: stand | crouch | swim | fly
 * - Action: idle | walk | run | attack | hurt | death
 *
 * Prevents invalid state vectors (e.g. water x fly x walk) and provides
 * deterministic track resolution for rendering layers.
 */
import type {
  CharacterSchemaType,
  EnvironmentType,
  StanceType,
  ActionType,
  AnimationTrack,
} from './character-schema'

export interface EntityStateVector {
  environment: EnvironmentType
  stance: StanceType
  action: ActionType
}

/**
 * Resolves the animation track for a given character schema, stance, and action.
 * Falls back gracefully to stand:idle or first available track if exact match is absent.
 */
export function resolveTrack(
  schema: CharacterSchemaType,
  stance: StanceType,
  action: ActionType
): AnimationTrack | null {
  const directKey = `${stance}:${action}`
  if (schema.tracks[directKey]) {
    return schema.tracks[directKey]
  }

  // Fallbacks within the same stance
  const stanceIdleKey = `${stance}:idle`
  if (schema.tracks[stanceIdleKey]) {
    return schema.tracks[stanceIdleKey]
  }

  // Fallbacks to standing stance
  const standKey = `stand:${action}`
  if (schema.tracks[standKey]) {
    return schema.tracks[standKey]
  }

  const defaultKey = 'stand:idle'
  if (schema.tracks[defaultKey]) {
    return schema.tracks[defaultKey]
  }

  // First available track
  const trackKeys = Object.keys(schema.tracks)
  return trackKeys.length > 0 ? schema.tracks[trackKeys[0]] : null
}

export class SpriteHSM {
  private readonly schema: CharacterSchemaType
  private environment: EnvironmentType
  private stance: StanceType
  private action: ActionType
  private animTimer: number = 0
  private isDead: boolean = false

  constructor(
    schema: CharacterSchemaType,
    initialEnvironment?: EnvironmentType,
    initialStance?: StanceType,
    initialAction: ActionType = 'idle'
  ) {
    this.schema = schema
    this.environment = initialEnvironment || schema.defaultEnvironment
    this.stance =
      initialStance ||
      (schema.allowedStances.includes('stand') ? 'stand' : schema.allowedStances[0]) ||
      'stand'
    this.action = initialAction
    this.validateAndNormalize()
  }

  /**
   * Sets the environment (e.g. ground, water, air). Automatically adapts stance.
   */
  setEnvironment(env: EnvironmentType): boolean {
    if (!this.schema.allowedEnvironments.includes(env)) {
      return false
    }
    this.environment = env

    // Automatically adapt stance if invalid for new environment
    if (env === 'water' && this.schema.allowedStances.includes('swim')) {
      this.stance = 'swim'
    } else if (env === 'air' && this.schema.allowedStances.includes('fly')) {
      this.stance = 'fly'
    } else if (env === 'ground' && (this.stance === 'swim' || this.stance === 'fly')) {
      this.stance = 'stand'
    }

    this.validateAndNormalize()
    return true
  }

  /**
   * Sets the stance (e.g. stand, crouch, swim, fly).
   */
  setStance(stance: StanceType): boolean {
    if (!this.schema.allowedStances.includes(stance)) {
      return false
    }
    this.stance = stance
    this.validateAndNormalize()
    return true
  }

  /**
   * Sets the action (e.g. idle, walk, run, attack, hurt, death).
   */
  setAction(action: ActionType): void {
    if (this.isDead && action !== 'death') {
      return
    }
    if (action === 'death') {
      this.isDead = true
    }
    if (this.action !== action) {
      this.action = action
      this.animTimer = 0
    }
  }

  /**
   * Advances the animation timer by delta time (in seconds).
   */
  update(dt: number): void {
    this.animTimer += dt
  }

  /**
   * Computes the current keyframe index for the active track.
   */
  getKeyframeIndex(): number {
    const track = this.getCurrentTrack()
    if (!track || track.frameCount <= 1) {
      return 0
    }

    const totalFrameProgress = Math.floor(this.animTimer * track.fps)
    return totalFrameProgress % track.frameCount
  }

  /**
   * Resolves the active animation track.
   */
  getCurrentTrack(): AnimationTrack | null {
    return resolveTrack(this.schema, this.stance, this.action)
  }

  getStateVector(): EntityStateVector {
    return {
      environment: this.environment,
      stance: this.stance,
      action: this.action,
    }
  }

  getHumanReadableState(): string {
    if (this.stance === 'stand') {
      return this.action.toUpperCase()
    }
    return `${this.stance.toUpperCase()} ${this.action.toUpperCase()}`
  }

  /**
   * Cycles to the next valid track for showroom inspection.
   */
  cycleNextTrack(): string {
    const trackKeys = Object.keys(this.schema.tracks)
    if (trackKeys.length === 0) {
      return 'IDLE'
    }

    const currentKey = `${this.stance}:${this.action}`
    const idx = trackKeys.indexOf(currentKey)
    const nextIdx = (idx + 1) % trackKeys.length
    const nextKey = trackKeys[nextIdx]
    const track = this.schema.tracks[nextKey]

    this.stance = track.stance
    this.action = track.action
    this.animTimer = 0
    return this.getHumanReadableState()
  }

  private validateAndNormalize(): void {
    if (!this.schema.allowedEnvironments.includes(this.environment)) {
      this.environment = this.schema.defaultEnvironment
    }
    if (!this.schema.allowedStances.includes(this.stance)) {
      this.stance = this.schema.allowedStances[0] || 'stand'
    }
  }
}
